// code adapted from "locale2" v2.3.1 https://github.com/moimikey/locale2
import type { TSFixMe } from '@policyfly/types/common'

function formatLocale (localeToFormat: unknown): string {
  if (typeof localeToFormat !== 'string') return 'en-US'
  return localeToFormat.split('-').slice(0, 2).map(function (chunk, index) {
    if (index !== 0 && chunk.length === 2) return chunk.toUpperCase()
    return chunk
  }).join('-')
}

function getLocale (): string {
  const windowLocale: Window & { chrome?: TSFixMe, Intl?: TSFixMe } = window

  let currentLocale = ''

  if (windowLocale.chrome && windowLocale.chrome.runtime && typeof windowLocale.chrome.runtime.getManifest === 'function') {
    const chromeLocale = windowLocale.chrome.runtime.getManifest()
    if (chromeLocale && chromeLocale.current_locale) {
      return chromeLocale.current_locale
    }
  }

  currentLocale = (windowLocale.navigator && (
    (windowLocale.navigator.languages && windowLocale.navigator.languages[0]) ||
    windowLocale.navigator.language ||
    // @ts-expect-error(external): Only exists in some browsers
    windowLocale.navigator.userLanguage
  ))

  if (!currentLocale && windowLocale.navigator && windowLocale.navigator.userAgent) {
    const userAgentLocale = windowLocale.navigator.userAgent.match(/;.(\w+-\w+)/i)
    if (userAgentLocale) return userAgentLocale[1]!
  }

  if (!currentLocale) {
    currentLocale = (windowLocale.clientInformation || /* istanbul ignore next */ Object.create(null)).language
  }

  if (!currentLocale) {
    if (windowLocale.Intl && typeof windowLocale.Intl.DateTimeFormat === 'function') {
      currentLocale = windowLocale.Intl.DateTimeFormat().resolvedOptions && windowLocale.Intl.DateTimeFormat().resolvedOptions().locale
    }
  }
  return currentLocale
}

export function locale (): string {
  return formatLocale(getLocale())
}

/**
 * The granularity of a date being entered or displayed.
 */
export enum LocaleDateMode {
  /**
   * A full date with day, month and year, e.g. `MM/DD/YYYY`.
   */
  DAY = 1,
  /**
   * A month and year only, e.g. `MM/YYYY`.
   * When parsed, the day is set to the 1st of the month.
   */
  MONTH = 2
}

/**
 * Return the date format based on the current locale.
 * Locales that allow a single digit for day/month are forced to use 2 for consistency.
 * Use `mode` to specify the granularity of date to return.
 *
 * @example
 * // LocaleDateMode.DAY
 * 'en-US' -> 'MM/DD/YYYY'
 * 'en-GB' -> 'DD/MM/YYYY'
 *
 * // LocaleDateMode.MONTH
 * 'en-US' -> 'MM/YYYY'
 * 'en-GB' -> 'MM/YYYY'
 */
export function localeDateFormat (mode: LocaleDateMode): string {
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: 'numeric',
  }
  switch (mode) {
    case LocaleDateMode.MONTH:
      break
    case LocaleDateMode.DAY:
    default:
      options.day = '2-digit'
  }

  return Intl.DateTimeFormat(locale(), options)
    .formatToParts()
    .map((p) => {
      switch (p.type) {
        case 'day': return 'DD'
        case 'month': return 'MM'
        case 'year': return 'YYYY'
        case 'literal': return p.value
        default: return ''
      }
    })
    .join('')
}

/**
 * Return the date format based on the current locale in a format that chrono supports.
 * Locales that allow a single digit for day/month are forced to use 2 for consistency.
 *
 * @example
 * 'en-US' -> '%m/%d/%Y'
 * 'en-GB' -> '%d/%m/%Y'
 */
export function chronoDateFormat (): string {
  return Intl.DateTimeFormat(locale(), {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
    .formatToParts()
    .map((p) => {
      switch (p.type) {
        case 'day': return '%d'
        case 'month': return '%m'
        case 'year': return '%Y'
        case 'literal': return p.value
        default: return ''
      }
    })
    .join('')
}
